/**
 * The registry of named controls a question may be rendered with.
 *
 * ## Why a registry and not a free-text field
 *
 * `uiComponent` names a component the BD frontend has to resolve. AgentHubPortal
 * resolves it through `CUSTOM_COMPONENTS[name]` and, when the name is not there,
 * renders the literal string `Custom component 'X' not found` — so a typo degrades
 * silently into a broken question rather than an error anyone sees. A free-text input
 * in the admin UI makes that typo easy; a list of what actually exists makes it
 * impossible.
 *
 * ## Why this list is short, and why it is a list at all
 *
 * One entry today. It exists as a list because the extension point in AgentHubPortal
 * is already a registry with three dead ends — the map is empty, the mapper never
 * produces `custom`, and the model had no field. This is the model's half of it; the
 * other two are a follow-up in that package. Keeping the shape right now means the
 * second component does not need a redesign.
 *
 * Descriptions are here, not in the component, because the ADMIN needs to know what a
 * question will do before choosing it — the behaviour, the data source and the ways it
 * can fail are the decision, not the name.
 */
export interface UiComponentSpec {
  /** The value stored in `QuestionnaireNode.uiComponent`. */
  name: string;
  /** Short label, for a card badge. */
  label: string;
  /** What the BD sees and does. */
  behaviour: string;
  /** Which service it reads, if any — the thing that can be down. */
  dataSource?: string;
  /** How it rejects bad input. */
  validation?: string;
}

export const UI_COMPONENTS: readonly UiComponentSpec[] = [
  {
    name: 'sfdc-account',
    label: 'SFDC 账户',
    behaviour:
      '两种形态：调用方名下有 SFDC 账户时是可筛选的下拉（文案为「公司名 (账户ID)」）；没有时退化成手输 18 位账户 ID。选中后写入 SfdcAccountContext，供后续题目和提交使用。',
    dataSource: 'gcr-company-service-client · GetSfdcAccountInfo（与 CPA 后端不是同一个服务）',
    validation:
      '手输时在「下一步」调 ValidateSfdcAccountId 远程校验。注意线上现状：题干写了「18 位」但代码没有任何长度校验，且校验失败与服务异常都返回 null，两者无法区分。',
  },
];

export const uiComponentSpec = (name: string | undefined): UiComponentSpec | undefined =>
  name ? UI_COMPONENTS.find((c) => c.name === name) : undefined;

/**
 * Short badge text for a card.
 *
 * Falls back to the raw name for a component that is not registered, rather than
 * hiding it: data can carry a name this build does not know, and showing it is how
 * that gets noticed.
 */
export const uiComponentLabel = (name: string): string => uiComponentSpec(name)?.label ?? name;
